import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TablePagination,
  TextField,
  MenuItem,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useGetSalesOrdersQuery } from '../../../store/api/salesApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

function SalesOrdersList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [toDate, setToDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const toApiDate = (d) =>
    d instanceof Date && !Number.isNaN(d.getTime())
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
          d.getDate(),
        ).padStart(2, '0')}`
      : undefined;
  const { data, isLoading, error } = useGetSalesOrdersQuery({
    page,
    limit: rowsPerPage,
    status: statusFilter || undefined,
    search: search || undefined,
    fromDate: toApiDate(fromDate),
    toDate: toApiDate(toDate),
  });
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';
  const dateFormat = company?.dateFormat || 'dd/MM/yyyy';

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2), maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)}).format(amount);
    } catch (e) {
      return `${currency} ${amount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    switch (dateFormat) {
      case 'MM/dd/yyyy':
        return `${mm}/${dd}/${yyyy}`;
      case 'yyyy-MM-dd':
        return `${yyyy}-${mm}-${yyyy}`;
      case 'dd-MM-yyyy':
        return `${dd}-${mm}-${yyyy}`;
      case 'dd/MM/yyyy':
      default:
        return `${dd}/${mm}/${yyyy}`;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      confirmed: 'info',
      shipped: 'warning',
      delivered: 'success',
      cancelled: 'error',
      on_hold: 'warning',
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    if (status === 'on_hold') return 'On hold';
    return status ? status.charAt(0).toUpperCase() + status.slice(1) : status;
  };

  if (isLoading) return <Typography>Loading...</Typography>;
  if (error) return <Typography color="error">Error loading sales orders</Typography>;

  const orders = data?.salesOrders || [];
  const pagination = data?.pagination;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Sales
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate('/sales/returns')} sx={{ borderRadius: 2 }}>
            Returns
          </Button>
          <Button variant="outlined" onClick={() => navigate('/sales/print')} sx={{ borderRadius: 2 }}>
            Print List
          </Button>
          <Button variant="outlined" onClick={() => navigate('/customers')} sx={{ borderRadius: 2 }}>
            Customers
          </Button>
          <Button variant="outlined" onClick={() => navigate('/sales/holds')} sx={{ borderRadius: 2 }}>
            Holds
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/sales/orders/new')} sx={{ borderRadius: 2 }}>
            New Sales Order
          </Button>
        </Box>
      </Box>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Card>
          <CardContent>
            <Box
            sx={{
              mb: 2,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <TextField
                size="small"
                label="Search (order / invoice)"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                sx={{ minWidth: 220 }}
              />
              <TextField
                size="small"
                select
                label="Status"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="">All statuses</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="on_hold">On hold</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="shipped">Shipped</MenuItem>
                <MenuItem value="delivered">Delivered</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </TextField>
              <DatePicker
                label="From date"
                value={fromDate}
                onChange={(newValue) => {
                  setFromDate(newValue);
                  setPage(1);
                }}
                slotProps={{ textField: { size: 'small' } }}
              />
              <DatePicker
                label="To date"
                value={toDate}
                onChange={(newValue) => {
                  setToDate(newValue);
                  setPage(1);
                }}
                slotProps={{ textField: { size: 'small' } }}
              />
            </Box>
            {pagination && (
              <Typography variant="body2" color="text.secondary">
                {`Page ${pagination.page} of ${pagination.pages || 1} (${pagination.total} total)`}
              </Typography>
            )}
          </Box>

            <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Order Number</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Order Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{`Total Amount (${currency})`}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Sales Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{`Balance Amount (${currency})`}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.length > 0 ? (
                  orders.map((order) => (
                    <TableRow key={order.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{order.orderNumber}</TableCell>
                      <TableCell>{order.customer?.name || '-'}</TableCell>
                      <TableCell>{formatDate(order.orderDate)}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'success.main' }}>
                        {formatAmount(order.totalAmount)}
                      </TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>{order.salesType}</TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 500,
                          color:
                            Number(order.balanceAmount || 0) > 0 ? 'warning.main' : 'text.secondary',
                        }}
                      >
                        {Number(order.balanceAmount || 0) > 0
                          ? formatAmount(order.balanceAmount)
                          : '–'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(order.status)}
                          color={getStatusColor(order.status)}
                          size="small"
                          sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/sales/orders/${order.id}`)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No sales orders found</Typography>
                      <Button variant="outlined" startIcon={<AddIcon />} onClick={() => navigate('/sales/orders/new')} sx={{ mt: 2 }}>
                        Create First Sales Order
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
            {pagination && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mt: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {`Page ${pagination.page} of ${pagination.pages || 1} (${pagination.total} total)`}
              </Typography>
              <TablePagination
                component="div"
                count={pagination.total}
                page={(pagination.page || 1) - 1}
                onPageChange={(_, newPage) => setPage(newPage + 1)}
                rowsPerPage={pagination.limit || rowsPerPage}
                onRowsPerPageChange={(e) => {
                  const next = parseInt(e.target.value, 10) || 10;
                  setRowsPerPage(next);
                  setPage(1);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rows per page:"
              />
            </Box>
            )}
          </CardContent>
        </Card>
      </LocalizationProvider>
    </Box>
  );
}

export default SalesOrdersList;
